import * as React from "react";
import { useLocation } from "wouter";
import { MapPin, ArrowRight, Package, Loader2, PackageCheck, Check } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { useSpaces } from "@/hooks/useSpaces.js";
import { cn } from "@/lib/utils.js";

/**
 * Unplaced & Re-locate lists (P0-B). One component, mounted in BOTH Organize and
 * Inventory (R-B3) — same query, same rows. The two lists are independent and
 * never merged:
 *   - Unplaced (R-B4): derived, items with no location at all (location == null).
 *   - Re-locate (R-B1): user-flagged "I want to move this", keeps its location.
 * Each row surfaces a suggested destination (R-B9) that is NEVER the item's
 * current location, and resolving deep-links into Find-a-home with the
 * suggestion prefilled. Multi-select applies one shared destination.
 */

export type ListMode = "unplaced" | "relocate";

interface ListRow {
  id: string;
  name: string;
  photoUrl: string | null;
  quantity: number;
  spaceId: string | null;
  spaceName: string | null;
  tags: string[];
  relocateAt: string | null;
  suggestion: { spaceId: string; spaceName: string; reason: string } | null;
}

export function UnplacedRelocateLists({ initialMode = "unplaced" }: { initialMode?: ListMode }) {
  const [, navigate] = useLocation();
  const toast = useToast();
  const { spaces } = useSpaces();
  const [mode, setMode] = React.useState<ListMode>(initialMode);
  const [rows, setRows] = React.useState<ListRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [bulkSpaceId, setBulkSpaceId] = React.useState("");
  const [bulkMoving, setBulkMoving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setSelectedIds([]);
    try {
      const data = await apiFetch<ListRow[]>(`/api/items/${mode === "unplaced" ? "unplaced" : "relocate"}`);
      setRows(data);
    } catch {
      setRows([]);
    }
    setLoading(false);
  }, [mode]);

  React.useEffect(() => { load(); }, [load]);

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // Resolve a single item through Find-a-home (P0-A loop) with its suggestion
  // prefilled (R-B9). The goals page reads ?suggest_item and opens find_home.
  const resolveOne = (row: ListRow) => {
    // Deep-link into Find-a-home with the item prefilled (?suggest_item). The
    // suggested destination is advisory and already shown on the row (R-B9); the
    // resolve flow is the P0-A placement loop (move-by-default + Undo).
    navigate(`/organize/goals?suggest_item=${row.id}`);
  };

  // Dismiss a Re-locate flag ("it's fine where it is", R-B6). No move.
  const dismissRelocate = async (row: ListRow) => {
    try {
      await apiFetch(`/api/items/${row.id}/relocate`, { method: "PUT", body: JSON.stringify({ relocate: false }) });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.show("Cleared — it's fine where it is.", "success", {
        label: "Undo",
        onClick: async () => {
          try { await apiFetch(`/api/items/${row.id}/relocate`, { method: "PUT", body: JSON.stringify({ relocate: true }) }); load(); }
          catch { toast.show("Couldn't undo", "error"); }
        },
      });
    } catch (e: any) { toast.show(e.message ?? "Failed to dismiss", "error"); }
  };

  // Multi-select → one shared destination (R-B7). A plain move + Undo.
  const bulkMove = async () => {
    if (!bulkSpaceId || selectedIds.length === 0 || bulkMoving) return;
    setBulkMoving(true);
    try {
      // Snapshot prior state for Undo. Besides the prior location (relocate items
      // have one; unplaced don't), we also record whether the item was flagged
      // Re-locate: a bulk move to a real space clears `relocate_at` server-side
      // (R-B5), so Undo must RE-FLAG those items or the move quietly drops the
      // "I want to move this" designation. Unplaced rows are never flagged.
      const prior = new Map<string, string | null>();
      const wasFlagged = new Map<string, boolean>();
      for (const id of selectedIds) {
        const r = rows.find((row) => row.id === id);
        prior.set(id, r?.spaceId ?? null);
        wasFlagged.set(id, r?.relocateAt != null);
      }
      const movedIds = [...selectedIds];
      await apiFetch(`/api/items/bulk`, { method: "PUT", body: JSON.stringify({ ids: movedIds, spaceId: bulkSpaceId }) });
      const targetName = spaces.find((s) => s.id === bulkSpaceId)?.name ?? "there";
      setBulkSpaceId("");
      await load();
      toast.show(`Moved ${movedIds.length} to ${targetName}`, "success", {
        label: "Undo",
        onClick: async () => {
          try {
            // Group by (prior space, was-flagged) so a single bulk PUT restores
            // both the location AND the Re-locate flag for each cohort. Passing
            // `relocate: true` alongside `spaceId` re-flags without the R-B5
            // auto-clear (explicit relocate intent wins over the clear).
            const byGroup = new Map<string, { spaceId: string | null; relocate: boolean; ids: string[] }>();
            for (const id of movedIds) {
              const spaceId = prior.get(id) ?? null;
              const relocate = wasFlagged.get(id) ?? false;
              const key = `${spaceId ?? "∅"}|${relocate}`;
              const g = byGroup.get(key) ?? { spaceId, relocate, ids: [] };
              g.ids.push(id);
              byGroup.set(key, g);
            }
            for (const { spaceId, relocate, ids } of byGroup.values()) {
              const undoBody: { ids: string[]; spaceId: string | null; relocate?: boolean } = { ids, spaceId };
              if (relocate) undoBody.relocate = true;
              await apiFetch(`/api/items/bulk`, { method: "PUT", body: JSON.stringify(undoBody) });
            }
            toast.show("Put back where they were.", "success");
            load();
          } catch { toast.show("Couldn't undo the move", "error"); }
        },
      });
    } catch (e: any) { toast.show(e.message ?? "Failed to move", "error"); }
    setBulkMoving(false);
  };

  const placeableSpaces = spaces.filter((s) => s.type !== "home");

  return (
    <div className="space-y-4">
      {/* Two independent lists, two entry tabs into the same component. */}
      <div className="flex gap-1 p-1 rounded-md bg-clay-muted/60 w-fit">
        {(["unplaced", "relocate"] as ListMode[]).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={cn(
              "px-4 py-1.5 rounded-md text-sm font-medium transition-colors",
              mode === m ? "bg-white text-amethyst-ink shadow-sm" : "text-body hover:text-amethyst-ink"
            )}
          >
            {m === "unplaced" ? "Unplaced" : "Re-locate"}
          </button>
        ))}
      </div>

      <p className="text-[0.8125rem] text-faint">
        {mode === "unplaced"
          ? "Things with no home yet. Give each one a spot."
          : "Things you flagged to move. They stay where they are until you re-home them."}
      </p>

      {/* Bulk bar — one shared destination for the selected items (R-B7). */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-md bg-amethyst-soft border border-clay-border">
          <span className="text-sm font-medium text-amethyst-ink tabular-nums">{selectedIds.length} selected</span>
          <span className="text-xs text-body">→ move all to</span>
          <select
            value={bulkSpaceId}
            onChange={(e) => setBulkSpaceId(e.target.value)}
            className="text-sm px-3 py-1.5 rounded-sm border border-clay-border bg-white text-body focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          >
            <option value="">Choose a space…</option>
            {placeableSpaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button
            onClick={bulkMove}
            disabled={!bulkSpaceId || bulkMoving}
            className="px-3 py-1.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
          >
            {bulkMoving ? "Moving…" : `Find a home for ${selectedIds.length}`}
          </button>
          <button onClick={() => setSelectedIds([])} className="text-xs text-body hover:text-amethyst-ink">Clear</button>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-lg bg-paper-raised animate-pulse" />)}
        </div>
      ) : rows.length === 0 ? (
        // Empty = a calm win, not an apology (DESIGN.md States).
        <div className="text-center py-12 rounded-lg bg-sage-wash border border-clay-border">
          <PackageCheck className="w-8 h-8 text-sage-deep mx-auto mb-2" />
          <p className="text-lg font-semibold text-ink" style={{ fontFamily: "Hanken Grotesk, system-ui, sans-serif" }}>
            {mode === "unplaced" ? "Everything has a home." : "Nothing flagged to move."}
          </p>
          <p className="text-sm text-body mt-1">
            {mode === "unplaced" ? "Every item has a spot right now." : "Flag an item from its detail view when you want to move it."}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-clay-border bg-white divide-y divide-clay-border">
          {rows.map((row) => (
            <div key={row.id} className="flex items-center gap-3 p-3">
              <button
                onClick={() => toggleSelect(row.id)}
                aria-label={selectedIds.includes(row.id) ? "Deselect" : "Select"}
                className={cn(
                  "shrink-0 w-5 h-5 rounded-sm border flex items-center justify-center transition-colors",
                  selectedIds.includes(row.id) ? "bg-primary border-primary text-white" : "border-clay-border hover:border-primary"
                )}
              >
                {selectedIds.includes(row.id) && <Check className="w-3.5 h-3.5" />}
              </button>
              <div className="shrink-0 w-10 h-10 rounded-md bg-paper-raised border border-clay-border overflow-hidden flex items-center justify-center">
                {row.photoUrl ? <img src={row.photoUrl} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-faint" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink truncate">{row.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {/* Re-locate rows show their CURRENT location (they have one). */}
                  {mode === "relocate" && row.spaceName && (
                    <span className="inline-flex items-center gap-1 text-[0.8125rem] text-amethyst-ink bg-amethyst-soft px-2 py-0.5 rounded-pill">
                      <MapPin className="w-3 h-3" />{row.spaceName}
                    </span>
                  )}
                  {/* Suggested destination inline (R-B9) — advisory accent, never
                      the current location; "no suggestion yet" when not confident. */}
                  {row.suggestion ? (
                    <span className="inline-flex items-center gap-1 text-[0.8125rem] text-body">
                      <ArrowRight className="w-3 h-3 text-sage-deep" />
                      Suggested: <span className="font-medium text-ink">{row.suggestion.spaceName}</span>
                    </span>
                  ) : (
                    <span className="text-[0.8125rem] text-faint">No suggestion yet</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {mode === "relocate" && (
                  <button
                    onClick={() => dismissRelocate(row)}
                    className="px-2.5 py-1.5 text-xs text-body hover:text-amethyst-ink hover:bg-amethyst-soft/60 rounded-md transition-colors"
                  >
                    It's fine here
                  </button>
                )}
                <button
                  onClick={() => resolveOne(row)}
                  className="px-3 py-1.5 rounded-md bg-primary text-white text-xs font-medium hover:bg-amethyst-ink transition-colors"
                >
                  Find a home
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
